import { useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { PortalModal, DemoNotice } from "@/components/portal/PortalModal";
import { StepType, StepProfile, StepLocation, StepPlan, StepConsent } from "@/components/portal/onboarding/steps";
import { provisionTenant, newIdempotencyKey, isLive } from "@/lib/platformClient";
import { useDialogs } from "@/lib/dialogs";
import { useI18n } from "@/i18n/I18nProvider";
import { IN_STATES } from "@/config/regions";
import { ILLUSTRATIVE_PLANS } from "@/config/plans";

const TITLES = ["Customer type", "Profile", "Location & language", "Plan", "Consent & verification", "Provisioning", "Application access"];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(step, f) {
  const e = {};
  if (step === 2) {
    if (f.name.trim().length < 2) e.name = "Name is required.";
    if (f.contact.trim().length < 2) e.contact = "Your name is required.";
    if (!EMAIL.test(f.email.trim())) e.email = "Enter a valid email.";
    if (!/^[6-9]\d{9}$/.test(f.mobile)) e.mobile = "Enter a 10-digit Indian mobile number.";
  }
  if (step === 3 && !f.city.trim()) e.city = "City is required.";
  if (step === 5 && !(f.consents.terms && f.consents.privacy)) e.consents = "Terms and data-processing consent are required.";
  return e;
}

const buildPayload = (f) => ({
  customer_type: f.type,
  profile: { display_name: f.name.trim(), contact_name: f.contact.trim(), email: f.email.trim(), mobile: `+91${f.mobile}`, team_size: f.type === "COMPANY" ? f.team : "1" },
  location: { country: "IN", state_code: f.state, city: f.city.trim() },
  preferences: { language: f.language, locale: `${f.language}-IN`, currency: "INR" },
  plan: { code: f.plan, billing_period: f.billing },
  consents: { ...f.consents, captured_at: new Date().toISOString() },
  source: "ayndri-portal",
});

const Progress = ({ step }) => (
  <div className="progress" aria-label={`Step ${step} of 7: ${TITLES[step - 1]}`} data-testid="onboard-progress">
    {TITLES.map((t, i) => (
      <span key={t} className="progress-item">{i > 0 && <span className="bar" />}<span className={`dot ${i + 1 <= step ? "on" : ""}`}>{i + 1}</span></span>
    ))}
  </div>
);

const defaultPlan = (type) => ILLUSTRATIVE_PLANS.find((p) => p.audience === type)?.code;

export const OnboardingWizard = ({ onClose, plan, billing = "monthly" }) => {
  const open = useDialogs();
  const { lang } = useI18n();
  const key = useRef(newIdempotencyKey());
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [f, setF] = useState({ type: plan === "SOLO" ? "INDIVIDUAL" : "COMPANY", name: "", contact: "", email: "", mobile: "", team: "2-5", state: "TG", city: "", language: lang, plan: plan || "GROWTH", billing, consents: { terms: false, privacy: false, updates: false } });
  const set = (k, v) => {
    setF((p) => {
      const n = { ...p, [k]: v };
      if (k === "type" && ILLUSTRATIVE_PLANS.find((x) => x.code === n.plan)?.audience !== v) n.plan = defaultPlan(v);
      return n;
    });
    setErrors({});
  };

  const next = () => {
    const e = validate(step, f);
    setErrors(e);
    if (!Object.keys(e).length) setStep(step + 1);
  };

  const provision = async () => {
    if (busy || result) return;
    setBusy(true);
    try {
      setResult(await provisionTenant(buildPayload(f), key.current));
      setStep(7);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const planName = ILLUSTRATIVE_PLANS.find((p) => p.code === f.plan)?.name;
  const stateName = IN_STATES.find((s) => s.code === f.state)?.name;
  const Steps = { 1: StepType, 2: StepProfile, 3: StepLocation, 4: StepPlan, 5: StepConsent };
  const Body = Steps[step];

  return (
    <PortalModal title={`Join Ayndri — ${TITLES[step - 1]}`} onClose={onClose} brand testId="onboarding-dialog">
      <Progress step={step} />
      {Body && <Body f={f} set={set} errors={errors} />}
      {step === 6 && (
        <div data-testid="onboard-provision-step">
          {!isLive("tenantProvisioning") && <DemoNotice>Provisioning will call the existing tenant-provisioning service once verified. Right now no tenant, user, subscription or CRM record is created.</DemoNotice>}
          <div className="kv" style={{ marginTop: 14 }}>
            <span>Profile</span><b>{f.name} · {f.type === "COMPANY" ? "Company" : "Freelancer"}</b>
            <span>Contact</span><b>{f.contact} · {f.email}</b>
            <span>Location</span><b>{f.city}, {stateName}, India</b>
            <span>Plan</span><b>{planName} · {f.billing}</b>
          </div>
          <details className="payload"><summary data-testid="onboard-payload-toggle">Request preview (no tenant_id: assigned by the server)</summary><pre data-testid="onboard-payload">{JSON.stringify(buildPayload(f), null, 2)}</pre></details>
        </div>
      )}
      {step === 7 && result && (
        <div className="done" data-testid="onboard-result">
          <div className="done-check">✓</div>
          <h3>{result.mode === "demo" ? "Demo onboarding preview complete" : "Workspace requested"}</h3>
          <p>Reference: <b data-testid="onboard-reference">{result.reference}</b></p>
          {result.mode === "demo" && <DemoNotice>No live account, tenant or payment was created. After integration, you'll sign in to the existing application to access your workspace.</DemoNotice>}
        </div>
      )}
      <div className="modal-actions">
        {step === 1 && <button className="btn outline" onClick={() => open("launcher")} data-testid="onboard-existing-customer-button">Existing customer? Open app</button>}
        {step > 1 && step < 7 && <button className="btn outline" onClick={() => setStep(step - 1)} disabled={busy} data-testid="onboard-back-button">Back</button>}
        {step < 6 && <button className="btn blue" onClick={next} data-testid="onboard-next-button">Continue <ArrowRight className="ic" /></button>}
        {step === 6 && <button className="btn blue" onClick={provision} disabled={busy} data-testid="onboard-provision-button">{busy ? "Submitting…" : "Submit for provisioning"}</button>}
        {step === 7 && <button className="btn blue" onClick={() => open("launcher")} data-testid="onboard-open-launcher-button">Open application launcher</button>}
      </div>
    </PortalModal>
  );
};
